import React from 'react';
import { useLocation } from 'react-router-dom';

import { IconFont, classNames } from 'fr-shared/components';
import { StandardMaterial } from 'fr-shared/components/part-graphics/material';
import { useUserAnalyticsContext } from 'fr-shared/context';

import { solidPartMaterial, xRayPartMaterial } from './DfmViewer';
import styles from './DfmViewer.module.css';

interface DfmViewerControlsProps {
  children?: React.ReactNode;
  partMaterial: StandardMaterial;
  resetCamera: () => void;
  setPartMaterial: (material: StandardMaterial) => void;
}

const DfmViewerControls = ({
  children,
  partMaterial,
  resetCamera,
  setPartMaterial,
}: DfmViewerControlsProps) => {
  const userAnalytics = useUserAnalyticsContext();
  const location = useLocation();
  const trackForStudio = location.pathname.includes('studio/evaluate');

  return (
    <div className={styles.DfmViewerControls}>
      <button
        className={classNames([
          styles.DfmViewerButton,
          partMaterial === xRayPartMaterial && styles.disabled,
        ])}
        onClick={() => {
          setPartMaterial(solidPartMaterial);
          trackForStudio && userAnalytics.track('Studio Part Viewer - X-Ray/Solid Button');
        }}
      >
        <IconFont name="circle" />
      </button>
      <button
        className={classNames([
          styles.DfmViewerButton,
          partMaterial === solidPartMaterial && styles.disabled,
        ])}
        onClick={() => {
          setPartMaterial(xRayPartMaterial);
          trackForStudio && userAnalytics.track('Studio Part Viewer - X-Ray/Solid Button');
        }}
      >
        <IconXray />
      </button>
      <button
        className={styles.DfmViewerButton}
        onClick={() => {
          resetCamera();
          trackForStudio && userAnalytics.track('Studio Part Viewer - Reset Camera');
        }}
      >
        <IconResetCamera />
      </button>
      {children}
    </div>
  );
};

const IconXray = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M4 11C3.45 11 3 11.45 3 12C3 12.55 3.45 13 4 13C4.55 13 5 12.55 5 12C5 11.45 4.55 11 4 11ZM4 15C3.45 15 3 15.45 3 16C3 16.55 3.45 17 4 17C4.55 17 5 16.55 5 16C5 15.45 4.55 15 4 15ZM4 7C3.45 7 3 7.45 3 8C3 8.55 3.45 9 4 9C4.55 9 5 8.55 5 8C5 7.45 4.55 7 4 7ZM1 7.5C0.72 7.5 0.5 7.72 0.5 8C0.5 8.28 0.72 8.5 1 8.5C1.28 8.5 1.5 8.28 1.5 8C1.5 7.72 1.28 7.5 1 7.5ZM4 3C3.45 3 3 3.45 3 4C3 4.55 3.45 5 4 5C4.55 5 5 4.55 5 4C5 3.45 4.55 3 4 3ZM19 8.5C19.28 8.5 19.5 8.28 19.5 8C19.5 7.72 19.28 7.5 19 7.5C18.72 7.5 18.5 7.72 18.5 8C18.5 8.28 18.72 8.5 19 8.5ZM12 5C12.55 5 13 4.55 13 4C13 3.45 12.55 3 12 3C11.45 3 11 3.45 11 4C11 4.55 11.45 5 12 5ZM12 1.5C12.28 1.5 12.5 1.28 12.5 1C12.5 0.72 12.28 0.5 12 0.5C11.72 0.5 11.5 0.72 11.5 1C11.5 1.28 11.72 1.5 12 1.5ZM1 11.5C0.72 11.5 0.5 11.72 0.5 12C0.5 12.28 0.72 12.5 1 12.5C1.28 12.5 1.5 12.28 1.5 12C1.5 11.72 1.28 11.5 1 11.5ZM8 18.5C7.72 18.5 7.5 18.72 7.5 19C7.5 19.28 7.72 19.5 8 19.5C8.28 19.5 8.5 19.28 8.5 19C8.5 18.72 8.28 18.5 8 18.5ZM8 1.5C8.28 1.5 8.5 1.28 8.5 1C8.5 0.72 8.28 0.5 8 0.5C7.72 0.5 7.5 0.72 7.5 1C7.5 1.28 7.72 1.5 8 1.5ZM8 5C8.55 5 9 4.55 9 4C9 3.45 8.55 3 8 3C7.45 3 7 3.45 7 4C7 4.55 7.45 5 8 5ZM8 10.5C7.17 10.5 6.5 11.17 6.5 12C6.5 12.83 7.17 13.5 8 13.5C8.83 13.5 9.5 12.83 9.5 12C9.5 11.17 8.83 10.5 8 10.5ZM16 11C15.45 11 15 11.45 15 12C15 12.55 15.45 13 16 13C16.55 13 17 12.55 17 12C17 11.45 16.55 11 16 11ZM16 15C15.45 15 15 15.45 15 16C15 16.55 15.45 17 16 17C16.55 17 17 16.55 17 16C17 15.45 16.55 15 16 15ZM16 7C15.45 7 15 7.45 15 8C15 8.55 15.45 9 16 9C16.55 9 17 8.55 17 8C17 7.45 16.55 7 16 7ZM16 3C15.45 3 15 3.45 15 4C15 4.55 15.45 5 16 5C16.55 5 17 4.55 17 4C17 3.45 16.55 3 16 3ZM19 11.5C18.72 11.5 18.5 11.72 18.5 12C18.5 12.28 18.72 12.5 19 12.5C19.28 12.5 19.5 12.28 19.5 12C19.5 11.72 19.28 11.5 19 11.5ZM12 15C11.45 15 11 15.45 11 16C11 16.55 11.45 17 12 17C12.55 17 13 16.55 13 16C13 15.45 12.55 15 12 15ZM12 18.5C11.72 18.5 11.5 18.72 11.5 19C11.5 19.28 11.72 19.5 12 19.5C12.28 19.5 12.5 19.28 12.5 19C12.5 18.72 12.28 18.5 12 18.5ZM8 6.5C7.17 6.5 6.5 7.17 6.5 8C6.5 8.83 7.17 9.5 8 9.5C8.83 9.5 9.5 8.83 9.5 8C9.5 7.17 8.83 6.5 8 6.5ZM8 15C7.45 15 7 15.45 7 16C7 16.55 7.45 17 8 17C8.55 17 9 16.55 9 16C9 15.45 8.55 15 8 15ZM12 10.5C11.17 10.5 10.5 11.17 10.5 12C10.5 12.83 11.17 13.5 12 13.5C12.83 13.5 13.5 12.83 13.5 12C13.5 11.17 12.83 10.5 12 10.5ZM12 6.5C11.17 6.5 10.5 7.17 10.5 8C10.5 8.83 11.17 9.5 12 9.5C12.83 9.5 13.5 8.83 13.5 8C13.5 7.17 12.83 6.5 12 6.5Z"
      fill="currentColor"
    />
  </svg>
);

const IconResetCamera = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 5.00003V2.21003C12 1.76003 11.46 1.54003 11.15 1.86003L7.35003 5.65003C7.15003 5.85003 7.15003 6.16003 7.35003 6.36003L11.14 10.15C11.46 10.46 12 10.24 12 9.79003V7.00003C15.73 7.00003 18.68 10.42 17.86 14.29C17.39 16.56 15.55 18.39 13.29 18.86C9.72003 19.61 6.54003 17.16 6.06003 13.85C5.99003 13.37 5.57003 13 5.08003 13C4.48003 13 4.00003 13.53 4.08003 14.13C4.70003 18.52 8.88003 21.77 13.61 20.85C16.73 20.24 19.24 17.73 19.85 14.61C20.84 9.48003 16.94 5.00003 12 5.00003Z"
      fill="currentColor"
    />
  </svg>
);

export default DfmViewerControls;
